import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { DICT } from "@/i18n/dict";
import { http, idr } from "@/lib/api";

const LangCtx = createContext(null);

export const LANGS = [
  { code: "id", label: "Indonesia", short: "ID", currency: "IDR", locale: "id-ID" },
  { code: "en", label: "English", short: "EN", currency: "USD", locale: "en-US" },
  { code: "ms", label: "Melayu", short: "MS", currency: "MYR", locale: "ms-MY" },
  { code: "fr", label: "Français", short: "FR", currency: "EUR", locale: "fr-FR" },
  { code: "de", label: "Deutsch", short: "DE", currency: "EUR", locale: "de-DE" },
  { code: "uk", label: "English (UK)", short: "UK", currency: "GBP", locale: "en-GB", text: "en" },
  { code: "au", label: "English (Australia)", short: "AU", currency: "AUD", locale: "en-US", text: "en" },
  { code: "sg", label: "English (Singapore)", short: "SG", currency: "SGD", locale: "en-US", text: "en" },
];

export const SPEAKS = { id: "Indonesia", en: "English", ms: "Malay", fr: "French", de: "German" };
export const speaksLang = (g, lang) => g.languages.includes(SPEAKS[lang]);
export const nativeGuides = (guides, lang) => (guides || []).filter((g) => g.role !== "driver" && speaksLang(g, lang));

function initialLanguage() {
  const saved = localStorage.getItem("ptg_lang");
  if (LANGS.some((option) => option.code === saved)) return saved;
  try {
    const locale = new Intl.Locale((navigator.languages?.[0] || navigator.language || "en-US").replace(/_/g, "-"));
    if (locale.language === "en") return { GB: "uk", AU: "au", SG: "sg" }[locale.region] || "en";
    return ["id", "ms", "fr", "de"].includes(locale.language) ? locale.language : "en";
  } catch {
    return "en";
  }
}

export function LangProvider({ children }) {
  const [sel, setSel] = useState(initialLanguage);
  const [fx, setFx] = useState(null);
  const [ratesFetchError, setRatesError] = useState(false);
  const retryRates = useCallback(() => {
    setRatesError(false);
    return http.get("/config").then(({ data }) => {
      if (!data.rates) throw new Error("Rates unavailable");
      setFx(data);
    }).catch(() => setRatesError(true));
  }, []);
  useEffect(() => { retryRates(); }, [retryRates]);
  const setLang = (l) => { localStorage.setItem("ptg_lang", l); setSel(l); };
  const meta = LANGS.find((l) => l.code === sel);
  const lang = meta.text || meta.code;
  useEffect(() => { document.documentElement.lang = lang; }, [lang]);
  const foreign = meta.currency !== "IDR";
  const ratesReady = !foreign || (Number.isFinite(fx?.rates?.[meta.currency]) && fx.rates[meta.currency] > 0);
  const ratesError = ratesFetchError || (foreign && !!fx && !ratesReady);
  const cityFactor = (city) => fx?.city_factors?.[city || fx.default_city] ?? fx?.markup;
  const factor = foreign && ratesReady ? cityFactor() * fx.rates[meta.currency] : 1;
  const t = (key) => DICT[lang][key] ?? DICT.en[key] ?? DICT.id[key] ?? key;
  const tr = (obj, field) => obj[`${field}_${lang}`] ?? obj[`${field}_en`] ?? obj[`${field}_id`];
  const levyOf = (city) => (foreign && fx ? fx.city_levies?.[city || fx.default_city] : null);
  const fmt = (v) => new Intl.NumberFormat(meta.locale, { style: "currency", currency: meta.currency, maximumFractionDigits: v < 10 ? 2 : 0 }).format(v);
  const pendingPrice = `${meta.currency} · ${t(ratesError ? "rates_unavailable" : "rates_loading")}`;
  const money = (n, city, levyQty = 0) => {
    if (!ratesReady) return pendingPrice;
    if (!foreign) return idr(n);
    return fmt((Number(n || 0) * cityFactor(city) + (levyOf(city)?.amount || 0) * levyQty) * fx.rates[meta.currency]);
  };
  const plain = (n) => !ratesReady ? pendingPrice : (foreign ? fmt(Number(n || 0) * fx.rates[meta.currency]) : idr(n));
  const fromIdr = (n) => !ratesReady ? "" : (foreign ? Math.round(n * factor) : n);
  const toIdr = (v) => (foreign ? Math.round(v / factor) : v);
  const currency = meta.currency;
  return <LangCtx.Provider value={{ lang, sel, setLang, t, tr, money, plain, levyOf, fromIdr, toIdr, currency, ratesReady, ratesError, retryRates, whatsappNumber: fx?.whatsapp_number }}>{children}</LangCtx.Provider>;
}

export const useLang = () => useContext(LangCtx);
